import { useNavigate } from 'react-router-dom';
import { useListInvestmentPlansQuery } from '../../features/investmentPlans/investmentPlansApi';
import Card, { CardHeader } from '../../components/ui/Card';
import PlanCard from '../../components/plans/PlanCard';
import PlanErrorState from '../../components/plans/PlanErrorState';
import EmptyState from '../../components/ui/EmptyState';
import { LayersIcon } from '../../components/ui/icons';
import { SkeletonCardGrid } from '../../components/ui/Skeleton';

/**
 * Real-backend Investment Plans catalog — GET /investor/investment-plans,
 * open to any authenticated user (no KYC/eligibility gate in front of
 * browsing). Subscribing to a plan has no endpoint yet, so this is
 * browse-only: no Create/Edit/Delete regardless of role, and no fake
 * Subscribe action — tapping a plan goes to a dedicated detail screen
 * (PlanDetail.jsx) with a disabled "coming soon" button instead.
 */
export default function Plans() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useListInvestmentPlansQuery();

  const plans = data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Investment plans</h1>
        <p className="text-foreground-muted text-sm mt-1">
          Browse the plans on offer. Subscribing isn't live yet — check back soon.
        </p>
      </div>

      <Card>
        <CardHeader title="Catalog" />

        {isLoading && <SkeletonCardGrid count={6} />}
        {isError && <PlanErrorState error={error} onRetry={refetch} />}
        {!isLoading && !isError && plans.length === 0 && (
          <EmptyState icon={LayersIcon} title="No investment plans available yet" body="Check back soon — new plans show up here." />
        )}

        {!isLoading && !isError && plans.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} onView={(p) => navigate(`/plans/${p.id}`)} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
